import ErrorBoundary from '@/components/common/ErrorBoundary';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import AccountLayout from '@/components/layouts/AccountLayout';
import AdminLayout from '@/components/layouts/AdminLayout';
import StoreLayout from '@/components/layouts/StoreLayout';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AdminRoute from './AdminRoute';
import ProtectedRoute from './ProtectedRoute';

/**
 * Retry function for lazy-loaded components
 * Implements exponential backoff for failed imports
 *
 * @param {Function} fn - Import function to retry
 * @param {number} retriesLeft - Number of retries remaining
 * @param {number} interval - Initial interval between retries in ms
 * @returns {Promise} Promise that resolves to the imported module
 */
const retryImport = (fn, retriesLeft = 3, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // On final retry failure, force reload to get fresh chunks
            console.error('All retries failed, reloading page...');
            reject(error);
            setTimeout(() => {
              window.location.reload();
            }, 50);
            return;
          }

          console.warn(
            `Import failed, retrying... (${retriesLeft - 1} attempts left)`
          );
          // Exponential backoff
          retryImport(fn, retriesLeft - 1, interval * 2).then(resolve, reject);
        }, interval);
      });
  });
};

/**
 * Creates a lazy-loaded component with retry logic
 *
 * @param {Function} importFn - Dynamic import function
 * @returns {React.LazyExoticComponent} Lazy component with retry capability
 */
const lazyWithRetry = (importFn) => {
  return lazy(() => retryImport(importFn));
};

// Lazy-loaded page components with retry logic
const AccountOrders = lazyWithRetry(() =>
  import('@/pages/account/AccountOrders')
);
const AccountProfile = lazyWithRetry(() =>
  import('@/pages/account/AccountProfile')
);
const AdminDashboard = lazyWithRetry(() =>
  import('@/pages/admin/AdminDashboard')
);
const ViewAuditLogs = lazyWithRetry(() =>
  import('@/pages/admin/audit-logs/ViewAuditLogs')
);
const AdminStoreDashboard = lazyWithRetry(() =>
  import('@/pages/admin/store/AdminStoreDashboard')
);
const ProductDetailView = lazyWithRetry(() =>
  import('@/pages/admin/store/ProductDetailView')
);
const ViewStoreCategories = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreCategories')
);
const ViewStoreOrders = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreOrders')
);
const ViewStoreOrdersPipeline = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreOrdersPipeline')
);
const ViewStoreProducts = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreProducts')
);
const ViewStoreReviews = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreReviews')
);
const ViewStoreCoupons = lazyWithRetry(() =>
  import('@/pages/admin/store/ViewStoreCoupons')
);
const ViewAdminUsers = lazyWithRetry(() =>
  import('@/pages/admin/users/ViewAdminUsers')
);
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const Login = lazyWithRetry(() => import('@/pages/Login'));
const Cart = lazyWithRetry(() => import('@/pages/store/Cart'));
const Checkout = lazyWithRetry(() => import('@/pages/store/Checkout'));
const CheckoutSuccess = lazyWithRetry(() =>
  import('@/pages/store/CheckoutSuccess')
);
const ProductDetail = lazyWithRetry(() =>
  import('@/pages/store/ProductDetail')
);
const ProductListing = lazyWithRetry(() =>
  import('@/pages/store/ProductListing')
);
const NotFound = lazyWithRetry(() => import('@/pages/NotFound'));

/**
 * Lazy loading error boundary component
 * Handles chunk loading failures by providing retry mechanism and user feedback
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactElement} Error boundary wrapper
 */
const LazyLoadErrorBoundary = ({ children }) => (
  <ErrorBoundary
    onError={(error, errorInfo) => {
      console.error('Lazy loading error:', error, errorInfo);

      // Log specific error types for better debugging
      if (
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes(
          'Failed to fetch dynamically imported module'
        ) ||
        error?.message?.includes('Importing a module script failed')
      ) {
        console.error(
          'Chunk loading failed - stale cache or network issue detected'
        );

        // Track in analytics if available
        if (window.gtag) {
          window.gtag('event', 'exception', {
            description: 'Chunk load error',
            fatal: false,
          });
        }
      }
    }}
    fallback={
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-900">
            Failed to Load Page
          </h2>
          <p className="text-gray-600 mb-6">
            There was a problem loading this page. This may be due to a network
            issue or an outdated version of the app.
          </p>
          <button
            onClick={() => {
              // Clear cache and reload
              if ('caches' in window) {
                caches.keys().then((names) => {
                  names.forEach((name) => caches.delete(name));
                });
              }
              window.location.reload();
            }}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Refresh Page
          </button>
        </div>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

/**
 * Main application routes component that defines all routes and handles authentication.
 * This component should be wrapped by BrowserRouter at the app's root level.
 *
 * Features:
 * - Lazy-loaded routes with automatic retry logic
 * - Protected routes with authentication checks
 * - Admin routes with role-based access control
 * - Proper error boundaries for chunk loading failures
 * - Suspense fallbacks for smooth loading states
 *
 * @component
 * @returns {React.ReactElement} Application routes with authentication checks
 *
 * @example
 * // In your main App.jsx:
 * import { BrowserRouter } from 'react-router-dom';
 * import AppRoutes from './routes/AppRoutes';
 *
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <AppRoutes />
 *     </BrowserRouter>
 *   );
 * }
 */
export const AppRoutes = () => {
  return (
    <div className="bg-gray-50">
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error('Router error:', error, errorInfo);
        }}
      >
        <Suspense fallback={<LoadingSpinner message="Loading..." />}>
          <LazyLoadErrorBoundary>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />

              {/* Root redirect - Protected */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />

              {/* Dashboard route - Protected */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Store Routes - Protected */}
              <Route
                path="/store"
                element={
                  <ProtectedRoute>
                    <StoreLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={<Navigate to="/store/products" replace />}
                />
                <Route path="products" element={<ProductListing />} />
                <Route path="products/:slug" element={<ProductDetail />} />
                <Route path="cart" element={<Cart />} />
                <Route path="checkout" element={<Checkout />} />
                <Route path="checkout/success" element={<CheckoutSuccess />} />
              </Route>

              {/* Account Routes - Protected */}
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={<Navigate to="/account/orders" replace />}
                />
                <Route path="orders" element={<AccountOrders />} />
                <Route path="profile" element={<AccountProfile />} />
              </Route>

              {/* Admin Routes - Protected with Admin Role Check */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route
                  index
                  element={<Navigate to="/admin/dashboard" replace />}
                />

                {/* Admin Dashboard */}
                <Route path="dashboard" element={<AdminDashboard />} />

                {/* Admin Store Management */}
                <Route path="store" element={<AdminStoreDashboard />} />
                <Route
                  path="store/categories"
                  element={<ViewStoreCategories />}
                />
                <Route path="store/products" element={<ViewStoreProducts />} />
                <Route
                  path="store/products/:id"
                  element={<ProductDetailView />}
                />
                <Route path="store/orders" element={<ViewStoreOrders />} />
                <Route
                  path="store/orders-pipeline"
                  element={<ViewStoreOrdersPipeline />}
                />
                <Route path="store/reviews" element={<ViewStoreReviews />} />
                <Route path="store/coupons" element={<ViewStoreCoupons />} />

                {/* Admin Users Management */}
                <Route path="users" element={<ViewAdminUsers />} />

                {/* Admin Audit Logs */}
                <Route path="audit-logs" element={<ViewAuditLogs />} />
              </Route>

              {/* 404 Fallback Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </LazyLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default AppRoutes;
